/* ===================================================================
   Import pipeline — accept dropped files (corpus / eval / workstream
   bundle), sniff the shape, normalize to the in-app data model, and
   build the preview shown by the import modal.
=================================================================== */
import { ENTITY_KINDS } from "./constants";
import { guessDocType, uid } from "./utils";
import { parseCsvRows, parseEmailFile, summarize } from "./corpusImport";
import { extractFromContent } from "./api/client";
import type { CorpusSet } from "./types";

export type ImportTargetType = "corpus" | "eval" | "workstream";

export function importExt(name: string): string {
  const m = /\.([A-Za-z0-9]+)$/.exec(String(name || ""));
  return m ? m[1].toLowerCase() : "";
}
export function importReadText(file: File): Promise<string> {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result || ""));
    r.onerror = () => rej(r.error);
    r.readAsText(file);
  });
}

/* Sniff a parsed-JSON shape → "corpus" | "eval" | "workstream" | null */
export function detectJsonType(data: any): ImportTargetType | null {
  if (data && typeof data === "object" && !Array.isArray(data)) {
    if (data.expected && typeof data.expected === "object") return "eval";
    if (Array.isArray(data.workstreams)) return "workstream";
    if (Array.isArray(data.emails)) return "corpus";
  }
  const obj = Array.isArray(data) ? data[0] : data;
  if (obj && typeof obj === "object") {
    const keys = Object.keys(obj).map((k) => k.toLowerCase());
    if (keys.includes("workstreams") || keys.includes("entities")) return "workstream";
    if (keys.includes("expected") || keys.includes("catalog")) return "eval";
    if (keys.some((k) => ["from", "sender", "subject", "to", "body", "raw", "message"].includes(k)))
      return "corpus";
  }
  return null;
}

/* fuzzy header → canonical field (Tier 2) */
export function fuzzyField(header: string): string | null {
  const h = String(header || "").toLowerCase().replace(/[^a-z]/g, "");
  const map: Array<[string, string[]]> = [
    ["from", ["from", "sender", "fromemail", "author", "sentby"]],
    ["fromName", ["fromname", "sendername", "name", "displayname"]],
    ["to", ["to", "recipient", "recipients", "toemail"]],
    ["cc", ["cc", "copy", "carboncopy"]],
    ["subject", ["subject", "title", "topic", "re"]],
    ["date", ["date", "time", "sent", "datetime", "timestamp", "sentat"]],
    ["body", ["body", "raw", "text", "message", "content", "email"]],
  ];
  for (const [field, al] of map) {
    if (al.some((a) => h === a || h.includes(a) || a.includes(h))) return field;
  }
  return null;
}

/* Tier 2 heuristic CSV → emails (non-standard headers) */
export function heuristicCsvEmails(text: string): any[] {
  const rows = parseCsvRows(text);
  if (rows.length < 2) return [];
  const header = rows[0];
  const idx: Record<string, number> = {};
  header.forEach((h, i) => {
    const f = fuzzyField(h);
    if (f && idx[f] == null) idx[f] = i;
  });
  const get = (r: string[], f: string): string => {
    const i = idx[f];
    return i != null && i < r.length ? String(r[i]).trim() : "";
  };
  return rows
    .slice(1)
    .map((r) => ({
      fromName: get(r, "fromName"),
      from: get(r, "from"),
      to: get(r, "to").split(/[;,]/).map((x) => x.trim()).filter(Boolean),
      cc: get(r, "cc").split(/[;,]/).map((x) => x.trim()).filter(Boolean),
      subject: get(r, "subject"),
      time: get(r, "date"),
      raw: get(r, "body"),
      attachments: [],
    }))
    .filter((e) => e.subject || e.raw || e.from);
}

/* Tier 2 heuristic CSV → eval expected map */
export function heuristicCsvEval(text: string): { expected: Record<string, any> } {
  const rows = parseCsvRows(text);
  if (rows.length < 2) return { expected: {} };
  const header = rows[0].map((h) => h.toLowerCase().trim());
  const find = (al: string[]): number =>
    header.findIndex((h) => al.some((a) => h.includes(a)));
  const idEmail = find(["email", "id", "#", "msg"]);
  const idCat = find(["catalog", "expected", "workstream", "matter", "result", "target"]);
  const flagCols: Array<[string, number]> = [
    ["conflict", find(["conflict"])],
    ["multi", find(["multi"])],
    ["privilege", find(["privilege", "priv"])],
    ["newpart", find(["new", "participant"])],
    ["phase", find(["phase"])],
    ["noise", find(["noise", "spam"])],
  ];
  const expected: Record<string, any> = {};
  rows.slice(1).forEach((r, i) => {
    const key =
      (idEmail >= 0 && r[idEmail] ? String(r[idEmail]).replace(/[^0-9]/g, "") : "") ||
      String(i + 1);
    const cat = idCat >= 0 && r[idCat] ? String(r[idCat]).trim() : "unassoc";
    const rec: Record<string, any> = { catalog: [cat] };
    flagCols.forEach(([f, ci]) => {
      if (ci >= 0 && r[ci] && /^(1|y|yes|true|x)$/i.test(String(r[ci]).trim())) rec[f] = true;
    });
    expected[key] = rec;
  });
  return { expected };
}

/* Normalizers into exact data-model shape ------------------------------- */
export function normalizeImportedEval(data: any, corpuses: CorpusSet[]): any {
  const src = Array.isArray(data) ? data[0] || {} : data || {};
  const expectedRaw = src.expected || {};
  const expected: Record<string, any> = {};
  Object.keys(expectedRaw).forEach((k) => {
    const v = expectedRaw[k] || {};
    const rec: Record<string, any> = {
      catalog: Array.isArray(v.catalog) ? v.catalog : v.catalog ? [v.catalog] : ["unassoc"],
    };
    ["conflict", "multi", "privilege", "newpart", "phase", "noise"].forEach((f) => {
      if (v[f] != null) rec[f] = !!v[f];
    });
    expected[k] = rec;
  });
  return {
    name: src.name || "Imported eval",
    corpusId: src.corpusId || (corpuses[0] || {}).id || "",
    expected,
  };
}
export function normalizeImportedBundle(data: any): any {
  const wsArr = Array.isArray(data)
    ? data
    : data.workstreams || (data.name || data.entities ? [data] : []);
  const workstreams = (wsArr || []).map((w: any) => ({
    id: "ws_" + uid(),
    name: w.name || "Imported workstream",
    code: w.code || "M-" + Math.floor(1000 + Math.random() * 9000),
    type: w.type || "Matter",
    phase: w.phase || "Setup",
    dealType: w.dealType || w.deal_type || "",
    entities: (w.entities || []).map((e: any) => ({
      id: "e_" + uid(),
      name: e.name || "Entity",
      kind: ENTITY_KINDS.includes(e.kind) ? e.kind : e.kind || "Investor",
      internal: !!e.internal || e.kind === "Our firm",
      people: (e.people || []).map((p: any) => ({
        name: p.name || "",
        email: p.email || "",
        role: p.role || "",
      })),
    })),
    memoFields: (w.memoFields || []).map((f: any) => ({ k: f.k || "", v: f.v || "" })),
    capTable: { attached: false, name: "" },
  }));
  return {
    name: (Array.isArray(data) ? "" : data.name) || "Imported bundle",
    note: (Array.isArray(data) ? "" : data.note) || "Imported via files",
    workstreams,
  };
}

/* Build preview objects for the confirm step --------------------------- */
export function previewFor(asType: ImportTargetType, payload: any): any {
  if (asType === "corpus") {
    const emails = payload.emails || [];
    const s = summarize(emails);
    const attWithFile = emails.reduce(
      (n: number, e: any) =>
        n + (e.attachments || []).filter((a: any) => a.fileData).length,
      0,
    );
    return {
      kind: "corpus",
      count: s.count,
      subjects: s.sampleSubjects,
      first: s.firstDate,
      last: s.lastDate,
      format: payload.format,
      attCount: s.attachmentCount,
      attWithFile,
      warnings: payload.warnings || [],
    };
  }
  if (asType === "eval") {
    const ex = payload.expected || {};
    const labeled = Object.keys(ex).length;
    const fset = new Set<string>();
    Object.values(ex).forEach((v: any) => {
      Object.keys(v).forEach((k) => {
        if (k !== "catalog" && v[k]) fset.add(k);
        if (k === "catalog") fset.add("catalog");
      });
    });
    return { kind: "eval", labeled, fields: [...fset] };
  }
  const wss = payload.workstreams || [];
  const ents = wss.reduce((n: number, w: any) => n + (w.entities || []).length, 0);
  const ppl = wss.reduce(
    (n: number, w: any) =>
      n + (w.entities || []).reduce((m: number, e: any) => m + (e.people || []).length, 0),
    0,
  );
  const deals = wss.map((w: any) => w.dealType).filter(Boolean);
  return { kind: "workstream", wsCount: wss.length, entities: ents, people: ppl, deals };
}

/* Frontend Tier-3 call into the backend LLM route */
export async function llmExtract(content: string, targetType: ImportTargetType): Promise<any> {
  const j = await extractFromContent({ content, targetType });
  if (!j.ok) throw new Error(j.error || "extraction failed");
  return j.data;
}

/* Process one file → import item. Decides the tier and the entity type. */
export async function processImportFile(
  file: File,
  targetType: ImportTargetType,
  corpuses: CorpusSet[],
  force: boolean,
): Promise<any> {
  const ext = importExt(file.name);
  const name = file.name.replace(/\.[^.]+$/, "");
  const finish = (
    asType: ImportTargetType,
    payload: any,
    status: string,
    tier: number,
    detectedType?: ImportTargetType,
  ) => ({
    asType,
    payload,
    preview: previewFor(asType, payload),
    status,
    tier,
    detectedType: detectedType || asType,
    name,
    format: payload.format || ext,
  });

  /* JSON: parse + auto-detect shape across all three entity types */
  if (ext === "json") {
    const data = JSON.parse(await importReadText(file));
    const detected = detectJsonType(data) || targetType;
    const asType: ImportTargetType = force ? targetType : detected;
    if (asType === "eval")
      return finish(
        "eval",
        normalizeImportedEval(data, corpuses),
        detected === "eval" ? "parsed" : "mapped",
        detected === "eval" ? 1 : 2,
        detected,
      );
    if (asType === "workstream")
      return finish(
        "workstream",
        normalizeImportedBundle(data),
        detected === "workstream" ? "parsed" : "mapped",
        detected === "workstream" ? 1 : 2,
        detected,
      );
    const r = await parseEmailFile(file, { guessDocType });
    return finish(
      "corpus",
      { emails: r.emails, format: r.format, warnings: r.warnings },
      detected === "corpus" ? "parsed" : "mapped",
      detected === "corpus" ? 1 : 2,
      detected,
    );
  }

  /* Known email containers → Tier 1 corpus */
  if (["mbox", "eml", "zip"].includes(ext)) {
    const r = await parseEmailFile(file, { guessDocType });
    return finish(
      "corpus",
      { emails: r.emails, format: r.format, warnings: r.warnings },
      "parsed",
      1,
    );
  }

  /* CSV → corpus (T1 standard / T2 fuzzy) or eval (T2 heuristic) */
  if (ext === "csv") {
    const text = await importReadText(file);
    if (targetType === "eval")
      return finish("eval", normalizeImportedEval(heuristicCsvEval(text), corpuses), "mapped", 2);
    if (targetType === "workstream")
      return finish(
        "workstream",
        normalizeImportedBundle(await llmExtract(text, "workstream")),
        "extracted",
        3,
      );
    let r: any;
    try {
      r = await parseEmailFile(file, { guessDocType });
    } catch {
      r = { emails: [], warnings: [] };
    }
    if (r.emails && r.emails.length)
      return finish(
        "corpus",
        { emails: r.emails, format: "csv", warnings: r.warnings },
        "parsed",
        1,
      );
    const ems = heuristicCsvEmails(text);
    if (ems.length)
      return finish("corpus", { emails: ems, format: "csv (mapped)", warnings: [] }, "mapped", 2);
    throw new Error("No rows could be mapped from this CSV.");
  }

  /* Anything else (pdf, txt, docx, md, …) → Tier 3 LLM extraction */
  const content = await importReadText(file);
  if (!content || !content.trim()) throw new Error("File is empty or unreadable as text.");
  const data = await llmExtract(content, targetType);
  if (targetType === "eval")
    return finish("eval", normalizeImportedEval(data, corpuses), "extracted", 3);
  if (targetType === "workstream")
    return finish("workstream", normalizeImportedBundle(data), "extracted", 3);
  const emails = Array.isArray(data) ? data : data.emails || [];
  return finish("corpus", { emails, format: "LLM", warnings: [] }, "extracted", 3);
}
